import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Warning, Plus, ArrowRight, MagnifyingGlass, Trash } from "@phosphor-icons/react";
import { usePerms } from "@/lib/perms";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-MX", {
      timeZone: "America/Mexico_City",
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch { return iso; }
}

export default function RechazosList() {
  const { has } = usePerms();
  const canCreate = has("evidence:write");
  const canDelete = has("appointments:delete");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rejection-records", { params: q ? { q } : {} });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar el rechazo ${r.folio}?`)) return;
    try {
      await api.delete(`/rejection-records/${r.id}`);
      toast.success("Rechazo eliminado");
      setItems((prev) => prev.filter((x) => x.id !== r.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-[#0055FF] mb-2">
            Rechazos · Registro Operativo
          </div>
          <h1 className="font-display text-4xl sm:text-5xl flex items-center gap-3">
            <Warning size={36} weight="duotone" className="text-[#FF3B30]" />
            Rechazos por Producto Maltratado
          </h1>
        </div>
        {canCreate && (
          <Link
            to="/rechazos/nuevo"
            data-testid="rch-new-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-sm bg-[#FF3B30] text-white text-sm font-semibold hover:brightness-110 active:scale-[0.98] transition-transform"
          >
            <Plus size={16} /> Nuevo Rechazo
          </Link>
        )}
      </div>

      <div className="bg-[#111111] border border-white/10 rounded-sm">
        <div className="p-4 border-b border-white/10 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md min-w-[220px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="rch-search"
              placeholder="Buscar por folio, proveedor, cita…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-10 pr-3 py-2.5 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none"
            />
          </div>
          <div className="ml-auto text-xs font-mono-data text-white/40">{items.length} registros</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[9px] font-mono-data uppercase tracking-widest text-white/40">
              <tr className="border-b border-white/10">
                <th className="text-left px-4 py-3">Folio</th>
                <th className="text-left px-4 py-3">Fecha</th>
                <th className="text-left px-4 py-3">Proveedor</th>
                <th className="text-left px-4 py-3">Cita RCN</th>
                <th className="text-left px-4 py-3">Elaborado por</th>
                <th className="text-right px-4 py-3">SKUs</th>
                <th className="text-right px-4 py-3">PZ</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="rch-rows">
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center text-white/50">Cargando…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="p-10 text-center text-white/50">Sin registros aún.</td></tr>
              ) : (
                items.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3 font-mono-data text-[#FF3B30] font-bold">{r.folio}</td>
                    <td className="px-4 py-3 text-white/70 whitespace-nowrap">{fmt(r.elaboration_date)}</td>
                    <td className="px-4 py-3 text-white/80">{r.provider_name}</td>
                    <td className="px-4 py-3 font-mono-data text-white/60">{r.appointment_folio || "—"}</td>
                    <td className="px-4 py-3 font-mono-data text-white/60">{r.elaborated_by}</td>
                    <td className="px-4 py-3 text-right font-mono-data text-white">{r.products_count || 0}</td>
                    <td className="px-4 py-3 text-right font-mono-data text-[#F59E0B] font-bold">{r.total_pieces || 0}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Link
                          to={`/rechazos/${r.id}`}
                          data-testid="rch-open-btn"
                          className="inline-flex items-center gap-1 text-[10px] font-mono-data uppercase tracking-widest text-[#0055FF] hover:text-white"
                        >
                          Abrir <ArrowRight size={10} />
                        </Link>
                        {canDelete && (
                          <button
                            onClick={() => remove(r)}
                            data-testid="rch-delete-btn"
                            className="p-1 rounded-sm text-white/40 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
                          >
                            <Trash size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
